import { createRootRoute, Outlet, Navigate, useNavigate } from "@tanstack/react-router"
import { NotificationProvider } from "@/components/providers/NotificationProvider"
import { Toaster } from "sonner"
import { EngaAICopilot } from "@/components/copilot/EngaAICopilot"
import { ScrollToTop } from "@/components/shared/ScrollToTop"
import React, { Component, useEffect, type ReactNode, type ErrorInfo } from "react"

class GlobalErrorBoundary extends Component<{ children: ReactNode }, { error: any }> {
  state: { error: any } = { error: null }
  static getDerivedStateFromError(error: any) {
    return { error }
  }
  componentDidCatch(error: any, info: ErrorInfo) {
    console.error("🔥 [GlobalErrorBoundary] Error:", error)
    console.error("🔥 [GlobalErrorBoundary] Component Stack:", info.componentStack)

    // Auto-reload once when a lazy chunk fails to load (e.g. after a new deploy)
    const isChunkLoadError =
      error instanceof TypeError &&
      (error.message.includes("Failed to fetch dynamically imported module") ||
        error.message.includes("Importing a module script failed") ||
        error.message.includes("error loading dynamically imported module"))

    if (isChunkLoadError) {
      const reloadKey = "chunk_load_reload_attempted"
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, "1")
        window.location.reload()
      }
    }
  }
  render() {
    if (this.state.error) {
      return (
        <div className="p-8 m-6 bg-red-50 border border-red-200 rounded-2xl max-w-4xl mx-auto text-left space-y-4">
          <h2 className="text-lg font-bold text-red-700">Preview Debug: Component Render Error</h2>
          <button 
            type="button" 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-red-600 text-white text-xs font-bold rounded-lg cursor-pointer"
          >
            Reload
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

function NotFoundRedirect() {
  const navigate = useNavigate()
  useEffect(() => {
    navigate({ to: "/", replace: true })
  }, [navigate])

  return <Navigate to="/" replace />
}

// import { useIOSKeyboardFix } from "@/hooks/useIOSKeyboardFix"

export const Route = createRootRoute({
  component: () => {
    // useIOSKeyboardFix()
    return (
      <GlobalErrorBoundary>
        <NotificationProvider>
          <ScrollToTop />
          <Outlet />
          <Toaster position="top-center" richColors />
          <EngaAICopilot />
        </NotificationProvider>
      </GlobalErrorBoundary>
    )
  },
  notFoundComponent: NotFoundRedirect,
  errorComponent: ({ error }: { error: any }) => {
    console.error("🔥 [Route errorComponent]:", error)
    return (
      <div className="p-8 text-red-700 font-mono text-xs">
        Router Error: {String(error?.message || error)}
      </div>
    )
  },
})
